'use client';

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import IconCarousel from '@/components/IconCarousel';

export default function BenefitsSection() {
  const { t } = useTranslation('home');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const benefits = [
    {
      icon: '🧫',
      title: t('benefits.cards.removesPesticides.title'),
      description: t('benefits.cards.removesPesticides.description'),
      size: 'large',
      bg: 'bg-primary-50',
      accent: 'text-primary',
    },
    {
      icon: '🦠',
      title: t('benefits.cards.killsBacteria.title'),
      description: t('benefits.cards.killsBacteria.description'),
      size: 'small',
      bg: 'bg-blue-pale',
      accent: 'text-blue-brand',
    },
    {
      icon: '✨',
      title: t('benefits.cards.chemicalFree.title'),
      description: t('benefits.cards.chemicalFree.description'),
      size: 'small',
      bg: 'bg-amber-50',
      accent: 'text-gold-dark',
    },
    {
      icon: '🌿',
      title: t('benefits.cards.naturalIngredients.title'),
      description: t('benefits.cards.naturalIngredients.description'),
      size: 'medium',
      bg: 'bg-primary-50',
      accent: 'text-primary',
    },
    {
      icon: '🛡️',
      title: t('benefits.cards.safeEveryDay.title'),
      description: t('benefits.cards.safeEveryDay.description'),
      size: 'medium',
      bg: 'bg-blue-pale',
      accent: 'text-blue-brand',
    },
    {
      icon: '💧',
      title: t('benefits.cards.easyToUse.title'),
      description: t('benefits.cards.easyToUse.description'),
      size: 'large',
      bg: 'bg-surface',
      accent: 'text-primary-light',
    },
  ];

  const handleBuyNow = () => {
    document.getElementById('product')?.scrollIntoView({ behavior: 'smooth' });
  };

  const floatingBadge = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.7, delay: 0.8 + i * 0.15, ease: [0.16, 1, 0.3, 1] }
    })
  };

  return (
    <>
      <motion.div
        custom={4}
        initial="hidden"
        animate="visible"
        variants={floatingBadge}
        className="w-full">
        <IconCarousel className="mx-auto w-full max-w-3xl lg:mx-auto lg:max-w-7xl" showControls={false} />
      </motion.div>
      <section
        id="benefits"
        ref={ref}
        // FIX 1: Scaled down mobile padding and added `overflow-x-clip` to prevent animation wobbles
        className="py-16 lg:py-16 bg-white overflow-x-clip"
        aria-labelledby="benefits-heading"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          {/* FIX 2: Reduced bottom margin on mobile to tighten up the layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mb-12 lg:mb-20">
            <motion.div
              className="col-span-1 lg:col-span-6"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="text-xs font-semibold tracking-widest uppercase text-muted mb-3 lg:mb-4 block">
                {t('benefits.label')}
              </span>
              <h2
                id="benefits-heading"
                // FIX 3: Scaled typography for the headline
                className="section-headline text-4xl sm:text-5xl lg:text-6xl font-display font-light text-foreground leading-tight"
              >
                {t('benefits.title.engineered')}{' '}
                <span className="italic pl-0.5 pr-2 text-gradient-green">{t('benefits.title.protection')}</span>
                <br />
                {t('benefits.title.crafted')}{' '}
                <span className="italic" style={{ color: 'var(--color-blue)' }}>
                  {t('benefits.title.purity')}
                </span>
              </h2>
            </motion.div>
            <motion.div
              className="col-span-1 lg:col-span-5 lg:col-start-8 flex flex-col justify-end mt-2 lg:mt-0"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p className="text-muted text-base lg:text-lg leading-relaxed font-light">
                {t('benefits.description')}
              </p>
            </motion.div>
          </div>

          {/* Asymmetric bento grid */}
          {/* FIX 4: Tightened the grid gaps on smaller screens */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-12 lg:mb-16">
            {benefits?.map((benefit, i) => (
              <motion.div
                key={benefit?.title}
                // FIX 5: Tamed the rotation slightly on mobile to prevent edge-clipping
                initial={{ opacity: 0, y: 40, rotate: i % 2 === 0 ? -0.5 : 0.5 }}
                animate={inView ? { opacity: 1, y: 0, rotate: 0 } : {}}
                transition={{
                  duration: 0.8,
                  delay: 0.1 + i * 0.1,
                  ease: [0.16, 1, 0.3, 1],
                }}
                // FIX 6: Disabled heavy hover rotation on touch devices for a smoother experience
                whileHover={{
                  y: typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches ? 0 : -6,
                  rotate: typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches ? 0 : (i % 2 === 0 ? 0.5 : -0.5),
                  transition: { duration: 0.3 },
                }}
                // FIX 7: Responsive padding for the cards (p-6 on mobile, p-10 on desktop)
                className={`${benefit?.bg} rounded-3xl lg:rounded-4xl p-6 sm:p-8 lg:p-10 border border-border cursor-default
                ${benefit?.size === 'large' ? 'md:col-span-2 lg:col-span-1' : ''}
              `}
              >
                <span className="text-4xl lg:text-5xl mb-4 lg:mb-6 block">{benefit?.icon}</span>
                <h3 className={`font-display text-xl lg:text-2xl font-semibold mb-2 lg:mb-3 ${benefit?.accent}`}>
                  {benefit?.title}
                </h3>
                <p className="text-muted text-xs lg:text-sm leading-relaxed">{benefit?.description}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            className="text-center px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.8 }}
          >
            <button
              onClick={handleBuyNow}
              // FIX 8: Made the button full-width on mobile for an easier touch target
              className="btn-primary w-full sm:w-auto px-8 py-4 lg:px-12 lg:py-5 rounded-2xl font-semibold text-base tracking-wide shadow-green-lg active:scale-[0.98] transition-transform"
              aria-label="Buy Dr. Detoxer"
            >
              {t('hero.buyNowPrice')}
            </button>
            {/* <p className="text-muted text-xs lg:text-sm mt-4">{t('benefits.freeDelivery')}</p> */}
          </motion.div>
        </div>
      </section>

    </>
  );
}